import { FormCommonProps } from "src/components/common/forms/types.ts";
import { DeepPartial } from "react-hook-form";
import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import { Delivery } from "src/api/generated/erp/db/types/tables/delivery.ts";
import i18n from "i18next";
import { FormSelectField } from "src/components/common/forms/fields/FormSelectField.tsx";
import { FormDateTimeField } from "src/components/common/forms/fields/FormDateTimeField.tsx";
import dayjs from "dayjs";
import { FormTextField } from "src/components/common/forms/fields/FormTextField.tsx";
import { requireRule } from "src/components/common/forms/validation.ts";
import { FormSection } from "src/components/common/forms/FormSection.tsx";
import { DeliveryEditApi, DeliveryEditApi_FormInitData } from "src/api/generated/erp/delivery/api/deliveryEditApi.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import { genericNullableValue } from "src/utils/genericNullableValue.ts";
import { PostalAddressDto } from "src/api/generated/erp/erpUtils/postalAddressDto.ts";
import { DeliveryPackageSource } from "src/api/generated/erp/delivery/model/deliveryPackageSource.ts";
import { FormCheckbox } from "src/components/common/forms/fields/FormCheckbox.tsx";
import { FormCountrySelectField } from "src/components/common/country/FormCountrySelectField.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface DeliveryFormProps extends FormCommonProps<number> {
	readonly?: boolean;
	initialization:
		| {
				type: "existingDelivery";
				deliveryId: number;
		  }
		| ({
				type: "newDelivery";
		  } & DeliveryFormNewDeliveryDefaults);
}

export interface DeliveryFormNewDeliveryDefaults {
	siteId: number;
	recipientDescription: string;
	senderAddress?: PostalAddressDto;
	deliveryAddress?: PostalAddressDto;
	deliveryMethodId?: number;
	transportRouteId?: number;
	cargoDriverId?: number;
	connectNewDeliveryToSource?: DeliveryPackageSource;
}

interface FormValues extends Delivery {}

export const DeliveryForm = (props: DeliveryFormProps) => {
	const { onCompleted, onFormEdited, initialization, readonly } = props;

	const deliveryId = initialization.type === "existingDelivery" ? initialization.deliveryId : undefined;

	const [storedDefaultValues, setStoredDefaultValues] = useStoredState({
		defaultValue: {
			cargoDriverId: genericNullableValue<number>(),
		},
		key: "12B932BA61966B645",
	});

	return (
		<AsyncForm
			fetch={() =>
				DeliveryEditApi.getFormInitData({
					deliveryId,
					siteId: initialization.type === "newDelivery" ? initialization.siteId : undefined,
				})
			}
			getDefaultValues={getDefaultValues}
			submit={submit}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			columns={3}
			getUseFormProps={({ delivery }) => ({
				disabled: readonly || delivery?.deliveryState === "DELIVERED",
			})}
			agentContext={{
				key: defineAgentUiContextKey("erp.delivery.delivery.form"),
				title: i18n.t("delivery"),
				pathSegments: [i18n.t("form")],
				data: {
					deliveryId: deliveryId ?? null,
					siteId: initialization.type === "newDelivery" ? initialization.siteId : null,
				},
			}}
			render={(contentParams) => <FormContent {...props} {...contentParams} />}
		/>
	);

	function getDefaultValues({
		delivery,
		cargoDriverOptions,
		site,
	}: DeliveryEditApi_FormInitData): DeepPartial<FormValues> {
		if (delivery != null) return delivery;
		else if (initialization.type === "newDelivery") {
			const plannedLoadingDate = dayjs().startOf("day").add(12, "hour").toISOString();
			const deliveryAddress = initialization.deliveryAddress;

			return {
				siteId: initialization.siteId,
				recipientDescription: initialization.recipientDescription,
				cargoDriverId:
					initialization.cargoDriverId ??
					cargoDriverOptions.find((o) => o.cargoDriverId === storedDefaultValues.cargoDriverId)
						?.cargoDriverId ??
					undefined,
				deliveryMethodId: initialization.deliveryMethodId,
				recipientContact: deliveryAddress?.contact ?? "",
				transportRouteId: initialization.transportRouteId,
				plannedLoadingDate: plannedLoadingDate,
				plannedUnloadingDate: plannedLoadingDate,

				senderAddress_1: initialization.senderAddress?.address1 ?? site.address_1,
				senderAddress_2: initialization.senderAddress?.address2 ?? site.address_2,
				senderPostalCode: initialization.senderAddress?.postalCode ?? site.postalCode,
				senderCity: initialization.senderAddress?.city ?? site.city,
				senderCountry: initialization.senderAddress?.country ?? "FINLAND",
				senderContact: initialization.senderAddress?.contact,
				senderAddressDescription_1: site.companyName,
				senderAddressDescription_2: site.description,

				deliveryName: deliveryAddress?.name,
				deliveryAddress_1: deliveryAddress?.address1,
				deliveryAddress_2: deliveryAddress?.address2,
				deliveryCity: deliveryAddress?.city,
				deliveryPostalCode: deliveryAddress?.postalCode,
				deliveryCountry: deliveryAddress?.country ?? "FINLAND",
			};
		} else {
			// should not happen
			console.error("Couldn't get default values for a new delivery");
			return {};
		}
	}

	async function submit(values: FormValues) {
		setStoredDefaultValues({
			cargoDriverId: values.cargoDriverId ?? null,
		});

		if (deliveryId == null) {
			return await DeliveryEditApi.insert({
				delivery: values,
				connectToPackageSource:
					initialization.type === "newDelivery" ? initialization.connectNewDeliveryToSource : undefined,
			});
		} else {
			await DeliveryEditApi.update({ delivery: values });
			return deliveryId;
		}
	}
};

interface FormContentProps
	extends DeliveryFormProps,
		AsyncFormContentParams<DeliveryEditApi_FormInitData, FormValues> {}

const FormContent = ({
	control,
	data: { deliveryMethodOptions, cargoDriverOptions, transportRouteOptions },
}: FormContentProps) => {
	return (
		<>
			<FormDateTimeField control={control} name={"plannedLoadingDate"} label={i18n.t("planned_loading_time")} />
			<FormDateTimeField
				control={control}
				name={"plannedUnloadingDate"}
				label={i18n.t("planned_unloading_time")}
			/>
			<FormCheckbox
				control={control}
				name={"isDeliverable"}
				label={i18n.t("deliverable")}
			/>
			<FormSelectField
				control={control}
				name={"deliveryMethodId"}
				label={i18n.t("delivery_method")}
				options={deliveryMethodOptions}
				getOptionKey={(option) => option.deliveryMethodId}
				getOptionLabel={(option) => option.deliveryMethodCode}
				startNewGridRow
			/>
			<FormSelectField
				control={control}
				name={"cargoDriverId"}
				label={i18n.t("cargo_driver")}
				options={cargoDriverOptions}
				getOptionKey={(option) => option.cargoDriverId}
				getOptionLabel={(option) => option.cargoDriverName}
			/>
			<FormTextField control={control} name={"trackingCode"} label={i18n.t("tracking_code")} />
			<FormTextField control={control} name={"note"} label={i18n.t("note")} multiline spanGridColumns />
			<FormSelectField
				control={control}
				name={"transportRouteId"}
				label={i18n.t("transport_route")}
				options={transportRouteOptions}
				getOptionKey={(o) => o.transportRouteId}
				getOptionLabel={(o) => o.name}
			/>
			<AddressForm control={control} type={"delivery"} label={i18n.t("delivery_address")} />
			<AddressForm control={control} type={"sender"} label={i18n.t("sender_address")} />
		</>
	);
};

interface AddressFormProps extends Pick<FormContentProps, "control"> {
	type: "sender" | "delivery";
	label: string;
}

const AddressForm = ({ control, type, label }: AddressFormProps) => {
	return (
		<FormSection label={label}>
			<FormTextField
				control={control}
				name={`${type}Address_1`}
				label={i18n.t("address_1")}
				rules={requireRule()}
			/>
			<FormTextField control={control} name={`${type}Address_2`} label={i18n.t("address_2")} />
			<FormTextField
				control={control}
				name={`${type}PostalCode`}
				label={i18n.t("postal_code")}
				rules={requireRule()}
				startNewGridRow
			/>
			<FormTextField control={control} name={`${type}City`} label={i18n.t("city")} rules={requireRule()} />
			<FormCountrySelectField
				control={control}
				name={`${type}Country`}
				label={i18n.t("country")}
				disableClearable
			/>
			<FormTextField
				control={control}
				name={type === "delivery" ? "recipientContact" : "senderContact"}
				label={i18n.t("contact")}
			/>
			{type === "delivery" && (
				<>
					<FormTextField control={control} name={"recipientDescription"} label={i18n.t("recipient")} />
					<FormTextField control={control} name={"deliveryName"} label={i18n.t("address_description")} />
				</>
			)}
			{type === "sender" && (
				<>
					<FormTextField
						control={control}
						name={"senderAddressDescription_1"}
						label={i18n.t("address_description_1")}
					/>
					<FormTextField
						control={control}
						name={"senderAddressDescription_2"}
						label={i18n.t("address_description_2")}
					/>
				</>
			)}
		</FormSection>
	);
};
